import {
  Alert,
  AlertIcon,
  Button,
  Grid,
  HStack,
  Icon,
  Text,
  VStack,
} from "@chakra-ui/react";
import { RiAddLine } from "@remixicon/react";
import { lazy, Suspense, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Empty } from "../../../components/card/Empty";
import { PageContainer } from "../../../components/container/PageContainer";
import { CardProductMyStore } from "../../../components/dashboard_reseller/CardProductMyStore";
import SearchInput from "../../../components/input/SearchInput";
import { MenuSort } from "../../../components/menu/MenuSort";
import Pagination from "../../../components/Pagination";
import { CardSkeleton } from "../../../components/skeleton/CardSkeleton";
import { myStoreNavsSupplier } from "../../../constants/PageNavs";
import { PaginationInterface } from "../../../constants/Pagination";
import { CardProductMyStoreInterface } from "../../../constants/Product";
import { SelectOption } from "../../../constants/SelectOption";
import useCustomToast from "../../../lib/useCustomToast";
import useScreenWidth from "../../../lib/useScreenWidth";
import { axiosSupplier } from "../../../utils/AxiosInstance";

const FilterDrawer = lazy(
  () => import("../../../components/drawer/FilterDrawer")
);

const CategoryFilter = lazy(
  () => import("../../../components/menu/CategoryFilter")
);

export const MyStoreSupplier = () => {
  const [data, setData] = useState<CardProductMyStoreInterface[] | undefined>(
    undefined
  );
  const [shippingCity, setShippingCity] = useState<string | null>("INITIATE");
  const [pagination, setPagination] = useState<PaginationInterface | undefined>(
    undefined
  );

  const [loaded, setLoaded] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [sort, setSort] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [filter, setFilter] = useState<SelectOption | undefined>(undefined);
  const navigate = useNavigate();
  const toast = useCustomToast();
  const sw = useScreenWidth();

  useEffect(() => {
    const categoryId = filter?.id ? filter?.id : "";
    axiosSupplier
      .get(
        `/v1/supplier/product?page=${page}&sort=${
          sort || ""
        }&search=${search}&categoryId=${categoryId}`
      )
      .then((res) => {
        setPagination(res.data.data.paginator);
        setShippingCity(res.data.data.data.shipping);
        setData(res.data.data.data.products);
      })
      .catch((err) => {
        toast({
          description: err?.response?.data?.message || "",
        });
      })
      .finally(() => {
        setLoaded(true);
      });
  }, [page, sort, search, filter]);

  const goToAddProduct = () => {
    navigate(`/supplier/mystore/product/add`);
  };

  const goToDetailProduct = (id: any) => {
    navigate(`/supplier/mystore/product/${id}`);
  };

  const goToProfileSupplier = () => {
    navigate("/supplier/profile");
  };

  return (
    <PageContainer navs={myStoreNavsSupplier}>
      <VStack
        className="mystore-supplier"
        w={"100%"}
        mt={4}
        gap={4}
        align={"stretch"}
      >
        {!shippingCity && (
          <Alert status="warning" borderRadius={"md"}>
            <HStack>
              <AlertIcon />

              <Text textStyle={"b-reg"}>
                Lengkapi profil supplier dan isi alamat pengiriman anda agar
                produk anda bisa terilhat dan ditambahkan oleh reseller.
                <span
                  className="complete-profile-text"
                  onClick={goToProfileSupplier}
                >
                  Lengkapi Profil
                </span>
              </Text>
            </HStack>
          </Alert>
        )}

        <SearchInput
          w={"100%"}
          placeholder="Cari produk toko..."
          onConfirm={(input) => {
            setSearch(input);
          }}
        />

        <HStack className="layout-list-product" w={"100%"} alignItems={"start"}>
          {sw > 768 && (
            <Suspense fallback={<CardSkeleton row={1} height={435} />}>
              <CategoryFilter
                onSelected={(selectedFilter) => {
                  setFilter(selectedFilter);
                }}
                onResetFilter={(reset) => {
                  setFilter(undefined);
                }}
              />
            </Suspense>
          )}

          <VStack
            className="layout-product-supplier"
            w={"100%"}
            px={sw > 768 ? 4 : 0}
            pb={sw > 768 ? 4 : 0}
            align={"stretch"}
          >
            <HStack w={"100%"} justify={"space-between"}>
              <Button
                w={"fit-content"}
                variant={"outline"}
                colorScheme="red"
                leftIcon={<Icon as={RiAddLine} />}
                onClick={goToAddProduct}
              >
                Tambah Produk
              </Button>

              <HStack gap={[1, 2, 4]}>
                {sw < 768 && (
                  <Suspense fallback={<CardSkeleton row={1} height={40} />}>
                    <FilterDrawer
                      onSelected={(select) => {
                        setFilter(select);
                      }}
                      onResetFilter={(reset) => {
                        setFilter(undefined);
                      }}
                    />
                  </Suspense>
                )}

                <MenuSort
                  onSelected={(selected) => {
                    setSort(`${selected?.note}`);
                  }}
                  children={undefined}
                />
              </HStack>
            </HStack>

            {filter && (
              <HStack gap={1}>
                <Text fontSize={[10, null, 12]}>
                  Menampilkan pencarian untuk filter
                </Text>

                <Text fontSize={[10, null, 12]} fontWeight={"semibold"}>
                  {`"${filter?.name}"`}
                </Text>
              </HStack>
            )}

            {!loaded ? (
              <CardSkeleton row={3} column={2} height={300} mt={4} />
            ) : (
              <VStack w={"100%"}>
                {data && data.length > 0 ? (
                  <Grid
                    w={"100%"}
                    templateColumns={"repeat(auto-fill, minmax(160px, 1fr))"}
                    gap={4}
                    mt={4}
                  >
                    {data &&
                      data.map((item) => (
                        <CardProductMyStore
                          key={item.id}
                          data={item}
                          onClickItem={(id) => {
                            goToDetailProduct(id);
                          }}
                        />
                      ))}
                  </Grid>
                ) : (
                  <Empty />
                )}

                {loaded && pagination && data && data.length > 0 && (
                  <Pagination
                    mt={4}
                    totalItems={pagination.total}
                    pageSize={pagination.per_page}
                    onPageChange={(page) => {
                      setPage(page);
                    }}
                  />
                )}
              </VStack>
            )}
          </VStack>
        </HStack>
      </VStack>
    </PageContainer>
  );
};
